import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@pericles/framework-frontend";

import {
  fetchRoadmapSegments,
  fetchRoadmapBlueprint,
  fetchRoadmapUseCaseDigestDetail,
  generateRoadmapApplicationDetails,
  type RoadmapApplicationDetails,
  type RoadmapApplicationBlueprint,
  type RoadmapSegmentRecord,
  type RoadmapUseCaseDigestDetail,
} from "../../api/roadmapClient";

import { InlineAlert } from "../InlineAlert";

function formatLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return value;
}

export function RoadmapApplicationDetailsPanel({ projectId }: { projectId: string }): JSX.Element {
  const [segments, setSegments] = useState<RoadmapSegmentRecord[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState("");
  const [details, setDetails] = useState<RoadmapApplicationDetails | null>(null);
  const [blueprint, setBlueprint] = useState<RoadmapApplicationBlueprint | null>(null);
  const [blueprintError, setBlueprintError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [digestDetailsByUseCaseId, setDigestDetailsByUseCaseId] = useState<Record<string, RoadmapUseCaseDigestDetail>>({});
  const [digestDetailsLoading, setDigestDetailsLoading] = useState<Record<string, boolean>>({});

  const selectedSegment = useMemo(
    () => segments.find((segment) => segment.id === selectedSegmentId),
    [segments, selectedSegmentId],
  );

  useEffect(() => {
    if (!projectId) return;
    let isActive = true;
    fetchRoadmapSegments(projectId)
      .then((loaded) => {
        if (!isActive) return;
        setSegments(loaded);
        setSelectedSegmentId((current) => current || loaded[0]?.id || "");
      })
      .catch((err) => {
        console.error(err);
        if (!isActive) return;
        setSegments([]);
      });
    return () => {
      isActive = false;
    };
  }, [projectId]);

  const handleLoadDigestDetail = useCallback(
    async (useCaseId: string) => {
      if (!projectId || !useCaseId || digestDetailsByUseCaseId[useCaseId]) return;
      setDigestDetailsLoading((current) => ({ ...current, [useCaseId]: true }));
      try {
        const response = await fetchRoadmapUseCaseDigestDetail(projectId, useCaseId);
        setDigestDetailsByUseCaseId((current) => ({ ...current, [useCaseId]: response.digest }));
      } catch (err) {
        console.error(err);
        setError(`Impossible de charger le digest ${useCaseId}.`);
      } finally {
        setDigestDetailsLoading((current) => ({ ...current, [useCaseId]: false }));
      }
    },
    [digestDetailsByUseCaseId, projectId],
  );

  useEffect(() => {
    if (!selectedSegmentId) {
      setDetails(null);
      setDigestDetailsByUseCaseId({});
      setDigestDetailsLoading({});
      return;
    }
    const nextSegment = segments.find((segment) => segment.id === selectedSegmentId) ?? null;
    setDetails(nextSegment?.detailsJson ?? null);
  }, [segments, selectedSegmentId]);

  const loadBlueprint = useCallback(async (segmentId: string) => {
    if (!segmentId) {
      setBlueprint(null);
      return;
    }
    setBlueprintError(null);
    try {
      const response = await fetchRoadmapBlueprint(segmentId);
      setBlueprint(response.blueprint ?? null);
    } catch (err) {
      console.error(err);
      setBlueprint(null);
      setBlueprintError("Impossible de charger le blueprint.");
    }
  }, []);

  useEffect(() => {
    if (!selectedSegmentId) {
      setBlueprint(null);
      return;
    }
    void loadBlueprint(selectedSegmentId);
  }, [loadBlueprint, selectedSegmentId]);

  const handleGenerateDetails = useCallback(
    async (force: boolean, autoFill = false) => {
      if (!selectedSegmentId || !projectId) return;
      setGenerating(true);
      setError(null);
      try {
        const response = await generateRoadmapApplicationDetails(projectId, selectedSegmentId, {
          force,
          autoFill,
        });
        if (!response.ok && response.reasons?.length) {
          setError(response.reasons.map((reason) => reason.message).join(" "));
        }
        const refreshed = await fetchRoadmapSegments(projectId);
        setSegments(refreshed);
        const updatedSegment = refreshed.find((segment) => segment.id === selectedSegmentId) ?? null;
        setDetails(updatedSegment?.detailsJson ?? null);
        await loadBlueprint(selectedSegmentId);
      } catch (err) {
        console.error(err);
        setError("Impossible de générer les détails d'application.");
      } finally {
        setGenerating(false);
      }
    },
    [projectId, selectedSegmentId],
  );

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <div>
          <div className="text-sm font-semibold text-slate-700">Application details</div>
          <p className="text-sm text-slate-600">
            Générez et consolidez les détails applicatifs pour le handoff Atlas → Forge.
          </p>
        </div>
        {error ? <InlineAlert tone="danger" title="Application details" description={error} /> : null}
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.35fr)_minmax(0,0.65fr)]">
          <div className="flex flex-col gap-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Applications</div>
            {segments.length ? (
              <ul className="space-y-2 text-sm">
                {segments.map((segment) => (
                  <li
                    key={segment.id}
                    className={`rounded border px-3 py-2 ${
                      segment.id === selectedSegmentId ? "border-slate-400 bg-slate-50" : "border-slate-200"
                    }`}
                  >
                    <button
                      type="button"
                      className="text-left text-sm font-semibold text-slate-700"
                      onClick={() => setSelectedSegmentId(segment.id)}
                    >
                      {segment.name}
                    </button>
                    <div className="text-xs text-slate-500">{(segment.useCaseIds ?? []).length} use case(s)</div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-slate-500">Aucune application pour ce projet.</div>
            )}
          </div>
          <div className="rounded border border-slate-200 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700">
                  {selectedSegment?.name ?? "Sélectionnez une application"}
                </div>
                <div className="text-xs text-slate-500">
                  {selectedSegment?.detailsGeneratedAt
                    ? `Généré le ${selectedSegment.detailsGeneratedAt}`
                    : "Aucun détail généré."}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="button button-primary button-sm"
                  onClick={() => void handleGenerateDetails(false)}
                  disabled={!selectedSegmentId || generating || Boolean(selectedSegment?.detailsInputsHash)}
                >
                  {generating ? "Génération..." : "Generate Application Details"}
                </button>
                {selectedSegment &&
                (!selectedSegment.purpose ||
                  !selectedSegment.solutionType ||
                  !selectedSegment.aiWhatItDoes ||
                  !selectedSegment.impact) ? (
                  <button
                    type="button"
                    className="button button-secondary button-sm"
                    onClick={() => void handleGenerateDetails(false, true)}
                    disabled={!selectedSegmentId || generating}
                  >
                    Auto-fill missing fields
                  </button>
                ) : null}
                <button
                  type="button"
                  className="button button-secondary button-sm"
                  onClick={() => {
                    if (window.confirm("Regénérer les détails de l'application ?")) {
                      void handleGenerateDetails(true);
                    }
                  }}
                  disabled={!selectedSegmentId || generating}
                >
                  Regenerate
                </button>
              </div>
            </div>
            {details ? (
              <div className="mt-4 space-y-4 text-sm text-slate-700">
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span>Stop reason: {details.stopReason}</span>
                  {selectedSegment?.detailsInputsHash ? (
                    <span>Inputs hash: {selectedSegment.detailsInputsHash}</span>
                  ) : null}
                </div>
                {selectedSegment?.detailsMissingInfo?.length ? (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Missing info</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                      {selectedSegment.detailsMissingInfo.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Executive summary</div>
                  <div>{formatLabel(details.structured.execSummary)}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Scope</div>
                  <div className="text-xs text-slate-600">In scope</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {details.structured.scope.in.length ? (
                      details.structured.scope.in.map((item) => <li key={item}>{item}</li>)
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                  <div className="mt-2 text-xs text-slate-600">Out of scope</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {details.structured.scope.out.length ? (
                      details.structured.scope.out.map((item) => <li key={item}>{item}</li>)
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Users & workflow</div>
                  <div>{formatLabel(details.structured.usersAndWorkflow)}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Data & integrations</div>
                  <div className="text-xs text-slate-600">Data sources</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {details.structured.dataAndIntegrations.dataSources.length ? (
                      details.structured.dataAndIntegrations.dataSources.map((item) => <li key={item}>{item}</li>)
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                  <div className="mt-2 text-xs text-slate-600">Integrations</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {details.structured.dataAndIntegrations.integrations.length ? (
                      details.structured.dataAndIntegrations.integrations.map((item) => <li key={item}>{item}</li>)
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                  <div className="mt-2 text-xs text-slate-600">Gaps</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {details.structured.dataAndIntegrations.gaps.length ? (
                      details.structured.dataAndIntegrations.gaps.map((item) => <li key={item}>{item}</li>)
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">AI architecture</div>
                  <div className="text-xs text-slate-600">Recommended approach</div>
                  <div>{formatLabel(details.structured.aiArchitecture.recommendedApproach)}</div>
                  <div className="mt-2 text-xs text-slate-600">Tooling</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {details.structured.aiArchitecture.tooling.length ? (
                      details.structured.aiArchitecture.tooling.map((item) => <li key={item}>{item}</li>)
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                  <div className="mt-2 text-xs text-slate-600">Non-goals</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {details.structured.aiArchitecture.nonGoals.length ? (
                      details.structured.aiArchitecture.nonGoals.map((item) => <li key={item}>{item}</li>)
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Libraries to build</div>
                  {details.structured.librariesToBuild.length ? (
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                      {details.structured.librariesToBuild.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-sm text-slate-500">—</div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Delivery plan</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {details.structured.deliveryPlan.length ? (
                      details.structured.deliveryPlan.map((item) => (
                        <li key={item.milestone}>
                          <div className="font-medium">{item.milestone}</div>
                          <div className="text-xs text-slate-500">{item.what}</div>
                          {item.risks.length ? (
                            <div className="text-xs text-slate-500">Risks: {item.risks.join(", ")}</div>
                          ) : null}
                        </li>
                      ))
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Value hypothesis</div>
                  <div className="text-xs text-slate-600">Value</div>
                  <div>{formatLabel(details.structured.valueHypothesis.value)}</div>
                  <div className="mt-2 text-xs text-slate-600">Confidence</div>
                  <div>{details.structured.valueHypothesis.confidence}</div>
                  <div className="mt-2 text-xs text-slate-600">Why</div>
                  <div>{formatLabel(details.structured.valueHypothesis.why)}</div>
                </div>
                {details.structured.openQuestions.length ? (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Open questions</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                      {details.structured.openQuestions.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">AI basis</div>
                  <div className="mt-1 text-xs text-slate-600">Digest use cases: {(details.structured.basis?.digest?.useCaseIds ?? []).join(", ") || "—"}</div>
                  <div className="text-xs text-slate-600">Digest inputs hashes: {(details.structured.basis?.digest?.inputsHashes ?? []).join(", ") || "—"}</div>
                  <div className="text-xs text-slate-600">
                    Digest coverage: {(details.structured.basis?.digest?.coverage?.isComplete ?? false) ? "Complete" : "Partial"}
                  </div>
                  <div className="text-xs text-slate-600">
                    Expected digest use cases: {(details.structured.basis?.digest?.coverage?.expectedUseCaseIds ?? []).join(", ") || "—"}
                  </div>
                  <div className="text-xs text-slate-600">
                    Missing digest use cases: {(details.structured.basis?.digest?.coverage?.missingUseCaseIds ?? []).join(", ") || "—"}
                  </div>
                  <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Digest details</div>
                  <ul className="mt-1 space-y-2 text-sm">
                    {(details.structured.basis?.digest?.coverage?.expectedUseCaseIds ?? []).length ? (
                      (details.structured.basis?.digest?.coverage?.expectedUseCaseIds ?? []).map((useCaseId) => {
                        const digestDetail = digestDetailsByUseCaseId[useCaseId];
                        const isLoading = Boolean(digestDetailsLoading[useCaseId]);
                        return (
                          <li key={useCaseId} className="rounded border border-slate-200 bg-slate-50 p-2">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-xs text-slate-700">{useCaseId}</span>
                              <button
                                type="button"
                                className="button button-secondary button-sm"
                                onClick={() => void handleLoadDigestDetail(useCaseId)}
                                disabled={isLoading || Boolean(digestDetail)}
                              >
                                {isLoading ? "Loading..." : digestDetail ? "Loaded" : "Load digest JSON"}
                              </button>
                            </div>
                            {digestDetail ? (
                              <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">
                                {JSON.stringify(digestDetail.digestJson, null, 2)}
                              </pre>
                            ) : null}
                          </li>
                        );
                      })
                    ) : (
                      <li className="text-xs text-slate-500">—</li>
                    )}
                  </ul>
                  <div className="text-xs text-slate-600">Digest evidence refs</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {(details.structured.basis?.digest?.evidenceFieldRefs ?? []).length ? (
                      (details.structured.basis?.digest?.evidenceFieldRefs ?? []).map((item) => <li key={item}>{item}</li>)
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                  <div className="mt-2 text-xs text-slate-600">Synthesis use cases: {(details.structured.basis?.synthesis?.useCaseIds ?? []).join(", ") || "—"}</div>
                  <div className="text-xs text-slate-600">Synthesis inputs hashes: {(details.structured.basis?.synthesis?.inputsHashes ?? []).join(", ") || "—"}</div>
                  <div className="mt-2 text-xs text-slate-600">Segmentation inputs hash: {details.structured.basis?.segmentation?.inputsHash || "—"}</div>
                </div>
                {details.detailMarkdown ? (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Detail markdown</div>
                    <pre className="mt-1 whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                      {details.detailMarkdown}
                    </pre>
                  </div>
                ) : null}

              </div>
            ) : (
              <div className="mt-4 text-xs text-slate-500">Aucun détail d'application enregistré.</div>
            )}
            <div className="mt-6 border-t border-slate-200 pt-4">
              <div className="text-sm font-semibold text-slate-700">Blueprint draft</div>
              {blueprintError ? <div className="mt-2 text-xs text-rose-600">{blueprintError}</div> : null}
              {blueprint ? (
                <div className="mt-3 space-y-3 text-sm text-slate-700">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span>Status: {blueprint.status}</span>
                    <span>Inputs hash: {blueprint.inputsHash}</span>
                  </div>
                  {blueprint.reasonsJson?.length ? (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Open questions / missing info
                      </div>
                      <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                        {blueprint.reasonsJson.map((reason, index) => (
                          <li key={`${reason.code}-${index}`}>{reason.message}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Blueprint JSON</div>
                    <pre className="mt-2 max-h-80 overflow-auto rounded bg-slate-50 p-3 text-xs text-slate-700">
                      {JSON.stringify(blueprint.blueprintJson, null, 2)}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-xs text-slate-500">Aucun blueprint généré pour cette application.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
